import React from "react";
import StatNumber from "@/components/StatNumber";
import UpdatedAt from "@/components/UpdatedAt";

export type Stat = {
  value: string;
  exact: number;
  prefix?: string;
  label: string;
  /** Real number to count up to on scroll-into-view; omit to skip the animation. */
  target?: number;
  /** Reproduces `value` (and every value on the way to it) from a raw number. */
  format?: (n: number) => string;
};

type Props = {
  heading: string;
  stats: Stat[];
  updatedAt: string | null;
  /** Wrapper classes — this is where each page's background/padding goes. */
  className?: string;
  headingClassName?: string;
  valueClassName?: string;
  labelClassName?: string;
  /** Border colour between tiles, e.g. "border-[#40AB6C]". */
  dividerClassName?: string;
  updatedAtClassName?: string;
};

// The four-across stats band, as it already appears on the D2C and VilvaChat
// pages. Those two grew inline and each hard-codes its own copy of this grid;
// this component exists so the Verification API and Digital Marketing bands
// don't become copies three and four.
//
// Everything that differs between pages is a colour, so it comes in as a
// className — the structure, the responsive 2-up/4-up grid, the divider logic
// and the <UpdatedAt> footnote stay in one place.
export default function StatsBand({
  heading,
  stats,
  updatedAt,
  className = "",
  headingClassName = "",
  valueClassName = "",
  labelClassName = "",
  dividerClassName = "",
  updatedAtClassName = "",
}: Props) {
  return (
    <section className={className}>
      <section className="px-3 md:px-0 md:container relative">
        <p
          data-aos="fade-up"
          data-aos-duration="1000"
          className={`text-center font-NeueMontreal text-[32px] md:text-[52px] font-normal leading-[120%] ${headingClassName}`}
        >
          {heading}
        </p>
        <div className="max-w-7xl mx-auto py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                // At 2-up the dividers run after tiles 0/1 (row rule) and
                // between the columns; at 4-up only the three column rules
                // remain. Matching the existing bands exactly.
                className={`px-4 md:px-12 py-6 ${dividerClassName} ${
                  i === 0
                    ? "border-b lg:border-b-0 border-r"
                    : i === 1
                      ? "border-b lg:border-b-0 lg:border-r"
                      : i === 2
                        ? "border-r lg:border-r"
                        : ""
                }`}
              >
                <p className={`text-center font-NeueMontreal text-4xl md:text-5xl ${valueClassName}`}>
                  <StatNumber
                    display={stat.value}
                    exact={stat.exact}
                    prefix={stat.prefix}
                    target={stat.target}
                    format={stat.format}
                  />
                </p>
                <p className={`mt-1 text-center font-Schibsted_Grotesk text-sm md:text-lg ${labelClassName}`}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
          <UpdatedAt
            iso={updatedAt}
            className={`mt-4 text-center font-Schibsted_Grotesk text-[12px] font-normal ${updatedAtClassName}`}
          />
        </div>
      </section>
    </section>
  );
}
